import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

function MessageBubble({ role, content, t }) {
  return (
    <div className={`tb-brain-msg tb-brain-msg-${role}`} data-testid={`brain-msg-${role}`}>
      <div className="tb-brain-msg-role">{role === "user" ? t("brain.you") : t("brain.brain")}</div>
      <div className="tb-brain-msg-body">
        {(content || "").split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </div>
  );
}

export default function Brain() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [current, setCurrent] = useState(null); // {id, messages}
  const [query, setQuery] = useState("");
  const [asking, setAsking] = useState(false);
  const [err, setErr] = useState("");
  const listRef = useRef(null);

  const canBrain = user && (user.role === "admin" || user.features?.journal_enabled === true);

  useEffect(() => {
    if (!canBrain) return;
    api.get("/brain/conversations").then((r) => setConversations(r.data.items || [])).catch(() => {});
  }, [canBrain]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [current]);

  const askQuery = async () => {
    const q = query.trim();
    if (!q) return;
    setAsking(true);
    setErr("");
    try {
      const r = await api.post("/brain/ask", {
        query: q,
        conversation_id: current?.id || null,
      });
      setQuery("");
      // Refresh current conversation
      const conv = await api.get(`/brain/conversations/${r.data.conversation_id}`);
      setCurrent(conv.data);
      // Refresh list
      const list = await api.get("/brain/conversations");
      setConversations(list.data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("brain.errors.generic"));
    } finally {
      setAsking(false);
    }
  };

  const loadConversation = async (cid) => {
    try {
      const r = await api.get(`/brain/conversations/${cid}`);
      setCurrent(r.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e) || t("brain.errors.generic"));
    }
  };

  const startNew = () => setCurrent(null);

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      askQuery();
    }
  };

  if (!canBrain) {
    return (
      <div className="tb-page tb-brain-page">
        <h1>{t("brain.title")}</h1>
        <div className="tb-card">
          <p className="tb-muted">{t("brain.emptyForRole")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tb-page tb-brain-page" data-testid="brain-page">
      <header className="tb-journal-header">
        <div>
          <h1>{t("brain.title")}</h1>
          <p className="tb-muted">{t("brain.subtitle")}</p>
        </div>
      </header>

      <div className="tb-brain-layout">
        <aside className="tb-brain-sidebar">
          <button
            type="button"
            className="tb-primary-btn"
            onClick={startNew}
            data-testid="brain-new-conversation"
            style={{ width: "100%" }}
          >
            {t("brain.newConversation")}
          </button>
          <div className="tb-brain-conv-list">
            {conversations.length === 0 && (
              <p className="tb-muted" style={{ fontSize: 13, padding: "12px 4px" }}>
                {t("brain.noConversations")}
              </p>
            )}
            {conversations.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`tb-brain-conv-item ${current?.id === c.id ? "active" : ""}`}
                onClick={() => loadConversation(c.id)}
                data-testid={`brain-conv-${c.id}`}
              >
                <strong>{c.title || t("brain.untitled")}</strong>
                <small className="tb-muted">{c.message_count} msg</small>
              </button>
            ))}
          </div>
        </aside>

        <main className="tb-brain-main">
          <div className="tb-brain-messages" ref={listRef} data-testid="brain-messages">
            {!current || (current.messages || []).length === 0 ? (
              <div className="tb-brain-suggestions">
                <p className="tb-muted">{t("brain.starterHint")}</p>
                <ul>
                  <li>{t("brain.starters.risks")}</li>
                  <li>{t("brain.starters.recent")}</li>
                  <li>{t("brain.starters.metrics")}</li>
                </ul>
              </div>
            ) : (
              current.messages.map((m, i) => (
                <MessageBubble key={i} role={m.role} content={m.content} t={t} />
              ))
            )}
          </div>

          <div className="tb-brain-composer">
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t("brain.inputPlaceholder")}
              rows={3}
              className="tb-journal-input"
              disabled={asking}
              data-testid="brain-input"
            />
            <div className="tb-journal-composer-row">
              <div style={{ flex: 1 }} />
              <button
                type="button"
                className="tb-primary-btn"
                onClick={askQuery}
                disabled={asking || !query.trim()}
                data-testid="brain-submit"
              >
                {asking ? t("brain.thinking") : t("brain.ask")}
              </button>
            </div>
            {err && <div className="tb-error" data-testid="brain-error">{err}</div>}
          </div>
        </main>
      </div>
    </div>
  );
}
